import { EditApplicationSync, UDF } from '@intellio/shared/models';

const synchronizedUdfByApplicationField = {
  accountNumber: 'CustomerAccountNumber',
  projectName: 'ProjectName',
} as const satisfies Partial<Record<keyof EditApplicationSync, string>>;

type SynchronizedApplicationField =
  keyof typeof synchronizedUdfByApplicationField;

const synchronizedApplicationFieldByUdf = new Map<
  string,
  SynchronizedApplicationField
>(
  Object.entries(synchronizedUdfByApplicationField).map(
    ([applicationField, udfId]) => [
      udfId,
      applicationField as SynchronizedApplicationField,
    ]
  )
);

function toSynchronizedValue(
  value: string | number | null | undefined
): string {
  return value == null ? '' : value.toString();
}

export function includeTopLevelSynchronizedApplicationFields(
  model: EditApplicationSync
): EditApplicationSync {
  return (
    Object.entries(synchronizedUdfByApplicationField) as [
      SynchronizedApplicationField,
      string,
    ][]
  ).reduce(
    (synchronizedModel, [applicationField, udfId]) => {
      const udfValue = model.applicationUDFs?.[udfId];

      if (udfValue !== undefined) {
        synchronizedModel[applicationField] =
          toSynchronizedValue(udfValue);
      }

      return synchronizedModel;
    },
    { ...model }
  );
}

export function setSynchronizedApplicationField(
  model: EditApplicationSync,
  applicationField: SynchronizedApplicationField,
  value: string | number | null | undefined
): void {
  const synchronizedValue = toSynchronizedValue(value);
  model[applicationField] = synchronizedValue;
  model.applicationUDFs ??= {};
  model.applicationUDFs[synchronizedUdfByApplicationField[applicationField]] =
    synchronizedValue;
}

export function createSynchronizedApplicationUpdate(
  udfs: UDF[]
): EditApplicationSync | null {
  const synchronizedUdfs = udfs.filter((udf) =>
    synchronizedApplicationFieldByUdf.has(udf.id)
  );

  if (synchronizedUdfs.length === 0) {
    return null;
  }

  const model = new EditApplicationSync();
  model.applicationUDFs = udfs.reduce<Record<string, string | number | null>>(
    (applicationUDFs, udf) => {
      applicationUDFs[udf.id] = udf.value ?? null;
      return applicationUDFs;
    },
    {}
  );

  return includeTopLevelSynchronizedApplicationFields(model);
}
